import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { finalize, take } from 'rxjs/operators';

import { ProgramService } from 'src/app/core/api/program.service';

import { RegionService } from '../../../core/api/region.service';
import { UtilitiesService } from '../../../core/api/utlities.service';
import { Program, Region } from '../../../shared/models';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import { selectAdminFiltersAndPage } from '../../../store/admin/admin.selectors';
import * as fromApp from '../../../store/index';
import { AdminTableBase } from '../admin-table-base/admin-table-base';

@Component({
  selector: 'fc-manage-programs-page',
  templateUrl: './manage-programs-page.component.html',
  styleUrls: ['./manage-programs-page.component.scss'],
})
export class ManageProgramsPageComponent extends AdminTableBase implements OnInit {
  destroyRef = inject(DestroyRef);
  regions: Array<Region>;
  dataSource: MatTableDataSource<Program> = new MatTableDataSource<Program>();
  @ViewChild('filterName', { static: false }) filterName: any;

  readonly PAGE_NAME: string = 'programs';
  displayedColumns: string[] = ['programName', 'isActive', 'status', 'severity', 'regions'];

  filterValues = {
    name: '',
    is_national: null,
    restriction_level: null,
    is_active: null,
    program_region: null,
  };

  defaultSortQuery = '&sort=name(asc)';
  sortQuery = this.defaultSortQuery;

  sortBy = {
    name: this.sortStates.ascending,
  };

  constructor(
    readonly router: Router,
    readonly store: Store<fromApp.AppState>,
    readonly programService: ProgramService,
    readonly spinnerService: SpinnerService,
    readonly regionService: RegionService,
    readonly utilitiesService: UtilitiesService
  ) {
    super();
  }

  ngOnInit(): void {
    this.regionService.getAllRegions().subscribe(allRegions => {
      this.regions = allRegions;
    });

    this.store
      .select(selectAdminFiltersAndPage)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        super.setUpAdminFilter(state.filters);
        this.changeFilterValueToNum('is_national');
        this.changeFilterValueToNum('restriction_level');
        this.changeFilterValueToNum('is_active');
        this.filterValues.program_region = this.filterValues['region'];
        this.initializeFilters();
        this.queryItems();
      });
  }

  getBaseQueryString(): string {
    return `?count=${this.paginationTotal}&offset=${this.offset}`;
  }

  getFullQueryString(): string {
    return this.getBaseQueryString() + this.sortQuery + this.filterQuery + this.regionQuery;
  }

  clearFilters(): void {
    if (this.getBaseQueryString() !== this.getFullQueryString()) {
      super.clearFilters();
      this.filterName.nativeElement.value = '';
      this.toggleSingleSort('name');
    }
  }

  editProgram(id) {
    this.router.navigate(['/admin/manage/program/edit', id]);
  }

  getRestrictionLevel(row) {
    if (row.restrictionLevel === 0) {
      return 'Low';
    } else if (row.restrictionLevel === 1) {
      return 'Medium';
    } else if (row.restrictionLevel === 2) {
      return 'High';
    } else {
      return 'None';
    }
  }

  showRegionText(row) {
    let regionNames = [];
    row.regions.forEach(region => regionNames.push(region.name));
    return regionNames.join('<br/>');
  }

  queryItems() {
    this.spinnerService.show('program-spinner');
    this.programService
      .getFilteredPrograms(this.getFullQueryString())
      .pipe(finalize(() => this.spinnerService.hide('program-spinner')))
      .subscribe(programResults => {
        this.dataSource = new MatTableDataSource(programResults.results);
        this.totalItems = programResults.total;
        this.setAllPages();
        this.setStartEndIndexes();
      });
  }
}
